<template>
  <b-row class="patient-id">
    <b-col class="px-0">
      <patient-identifier-input
        ref="identifierInput"
        :patient-identifier="identifierToSave"
        :editing-identifier="editingIdentifier"
        :patient-identifier-types="patientIdentifierTypesForSelection"
        :centers="centers"
        :validation-control="errorMessage"
        @identifierTypeSelected="onIdentifierTypeSelected"
      >
        <vt-loading-button
          v-if="editingIdentifier"
          qa="patient-identifier-list-input-update-button"
          class="btn__outline btn-sm btn-block"
          :params="{ promise: submitEditIdentifier }"
        >
          <span>{{ $t('global.edit') }}</span>
        </vt-loading-button>
        <vt-loading-button
          v-else
          qa="patient-identifier-list-input-add-button"
          class="btn__outline btn-sm btn-block"
          :params="{ promise: submitAddIdentifier }"
        >
          <span>{{ $t('global.add') }}</span>
        </vt-loading-button>
      </patient-identifier-input>
      <div class="px-3">
        <hr class="mt-1">
      </div>
      <div class="crud-container">
        <div class="crud-id px-3">
          <div
            v-if="errorMessage"
            class="pb-2 error-message"
          >
            <!-- El siguiente mensaje aparece si intentas añadir y hay campos vacíos -->
            <span
              v-if="duplicationError"
              class="error"
            >{{ $t('component.patientForm.identifierDuplicated') }}</span>
            <span
              v-else
              class="error"
            >{{ $t('component.patientForm.errorMessage') }}</span>
          </div>
          <!-- El siguiente mensaje aparece si al guardar no hay identificadores asignados al paciente -->
          <div
            v-if="noIdentifiers"
            class="pb-2 error-message"
          >
            <span class="error">{{ $t('component.patientForm.idMessage') }}</span>
          </div>
          <div
            v-if="patientIdentifiers.length === 0"
            class="pb-2 error-message"
          >
            <span>{{ $t('component.patientForm.noIdAssigned') }}</span>
          </div>
          <b-row
            v-if="internalPatientIdentifiers"
            style="padding: 0 35px 0 28px;"
          >
            <b-col
              v-for="(identifier, key) in internalPatientIdentifiers"
              :key="identifier.internalId"
              cols="12"
              class="crud-element mb-3"
            >
              <patient-identifier-item
                ref="inputs"
                :patient-identifier="identifier"
              >
                <b-button
                  v-testing="'edit-item'"
                  :disabled="identifier.identifierType.code === PATIENT_AUTOGENERATED_GLOBAL_ID"
                  class="btn__icon btn__icon--primary px-1 py-0"
                  @click="editIdentifier(identifier)"
                >
                  <i
                    class="far fa-pen"
                    aria-hidden="true"
                  />
                </b-button>
                <b-button
                  v-testing="'remove-item'"
                  :disabled="identifier.identifierType.code === PATIENT_AUTOGENERATED_GLOBAL_ID"
                  class="btn__icon btn__icon--primary pl-1 pr-2 py-0"
                  @click="removeIdentifier(key)"
                >
                  <i
                    class="far fa-times"
                    aria-hidden="true"
                  />
                </b-button>
              </patient-identifier-item>
            </b-col>
          </b-row>
        </div>
      </div>
    </b-col>
  </b-row>
</template>

<script>

import PatientIdentifierInput from '@/components/patient/identifier/PatientIdentifierInput'
import PatientIdentifierItem from '@/components/patient/identifier/PatientIdentifierItem'
import patientIdentifierResource, { PATIENT_AUTOGENERATED_GLOBAL_ID } from '@/api/resources/PatientIdentifierResource'
import centerResource from '@/api/resources/common/commonCenterService'
import patientResource from '@/api/resources/PatientResource'
import { IdentifierTypeScope } from '@/api/resources/common/initialization/common-initialization.dto'

export default {
    name: 'PatientIdentifierListInput',
    components: {
        PatientIdentifierInput,
        PatientIdentifierItem
    },
    props: {
        patientId: { required: false, type: Number },
        patientIdentifiers: { type: Array, default: () => [] },
        inputCols: { type: String, default: '' }
    },
    data() {
        return {
            PATIENT_AUTOGENERATED_GLOBAL_ID: PATIENT_AUTOGENERATED_GLOBAL_ID,
            idCounter: 0,
            identifierToSave: {
                identifierType: {}
            },
            noIdentifiers: false,
            errorMessage: false,
            editingIdentifier: false,
            patientIdentifierTypes: [],
            internalPatientIdentifiers: [],
            patientIdentifierTypesForSelection: [],
            centers: [],
            duplicationError: false,
            defaultIdentifierType: { idtId: null }
        }
    },
    async created() {
        await this.getCenters()
        this.patientIdentifierTypes = await patientIdentifierResource.index()
        this.patientIdentifierTypesForSelection = this.patientIdentifierTypes.filter((identifier) => identifier.code !== PATIENT_AUTOGENERATED_GLOBAL_ID)
        this.defaultIdentifierType = this.patientIdentifierTypes.find(value => value.code === this.globalParameters.mainPatientIdentifierType.code) || { idtId: null }
        this.resetIdentifierToSave()
        this.internalPatientIdentifiers = this.patientIdentifiers.map(patientIdentifier => {
            return Object.assign({
                identifierType: this.findIdentifierType(patientIdentifier.identifierTypeId),
                paiId: patientIdentifier.paiId,
                identifierValue: patientIdentifier.paiIdentifier,
                center: this.findCenter(patientIdentifier.centerId)
            },
            !patientIdentifier.internalId && {
                internalId: this.generateInternalId()
            })
        })
    },
    methods: {
        resetIdentifierToSave() {
            this.identifierToSave = {
                identifierType: this.defaultIdentifierType
            }
        },
        async getCenters() {
            this.centers = await centerResource.getActiveCenters()
        },
        findIdentifierType(identifierTypeId) {
            return this.patientIdentifierTypes.find(identifier => {
                return identifier.idtId === identifierTypeId
            })
        },
        findCenter(id) {
            return this.centers.find(center => center.id === id)
        },
        async validate() {
            if (!this.internalPatientIdentifiers.length) {
                this.noIdentifiers = true
                return false
            }
            this.noIdentifiers = false
            return true
        },
        generateInternalId() {
            return ++this.idCounter
        },
        onIdentifierTypeSelected() {
            this.duplicationError = false
            this.errorMessage = false
        },
        editIdentifier(identifier) {
            this.duplicationError = false
            this.errorMessage = false
            if (this.identifierToSave === identifier) {
                this.resetIdentifierToSave()
                this.$nextTick(() => {
                    this.identifierToSave = identifier
                    this.$refs.identifierInput.errors.clear()
                })
            } else {
                this.identifierToSave = identifier
            }
            this.editingIdentifier = true
        },
        async checkNoDuplicatesInPatientIdentifiers(newPatientIdentifier) {
            let foundIdentifier = this.internalPatientIdentifiers.find(patientIdentifier => this.areDuplicatedPatientIdentifiers(patientIdentifier, newPatientIdentifier))
            this.duplicationError = !!foundIdentifier
            if (!this.duplicationError) {
                const [err] = await this.toCall(patientResource.checkDuplicate({
                    patientId: this.patientId,
                    patientIdentifierId: newPatientIdentifier.paiId,
                    paiIdentifier: newPatientIdentifier.identifierValue,
                    identifierTypeId: newPatientIdentifier.identifierType.idtId,
                    centerId: (newPatientIdentifier.center && newPatientIdentifier.center.id)
                }))
                if (err) {
                    this.showErrorModalGlobal(this.$t('component.patientForm.patientIdentifierAlreadyInUse', { identifierValue: newPatientIdentifier.identifierValue }))
                    this.duplicationError = true
                }
            }
        },
        areDuplicatedPatientIdentifiers(patientIdentifier, newPatientIdentifier) {
            if (patientIdentifier.identifierType.idtId === newPatientIdentifier.identifierType.idtId) {
                return patientIdentifier.identifierType.idtScope !== IdentifierTypeScope.CENTER_PATIENT || patientIdentifier.center.id === newPatientIdentifier.center.id
            }
            return false
        },
        async submitAddIdentifier() {
            const isValid = await this.$refs.identifierInput.validate()
            this.errorMessage = !isValid
            if (this.errorMessage) {
                this.duplicationError = false
                return
            }
            const newPatientIdentifier = this.$refs.identifierInput.getIdentifierInput()
            await this.checkNoDuplicatesInPatientIdentifiers(newPatientIdentifier)
            this.errorMessage = this.duplicationError
            if (this.errorMessage) {
                return
            }
            newPatientIdentifier.internalId = this.generateInternalId()
            this.internalPatientIdentifiers.push(newPatientIdentifier)
            this.consolidateInternalIdentifiers()
            this.resetIdentifierToSave()
            await this.$refs.identifierInput.resetValidator()
        },
        async submitEditIdentifier() {
            const isValid = await this.$refs.identifierInput.validate()
            this.errorMessage = !isValid
            if (this.errorMessage) {
                return
            }
            const editedElementIndex = this.internalPatientIdentifiers.findIndex(patientIdentifier => patientIdentifier.internalId === this.identifierToSave.internalId)
            const originalElement = this.internalPatientIdentifiers[editedElementIndex]
            this.internalPatientIdentifiers.splice(editedElementIndex, 1)
            const editedPatientIdentifier = this.$refs.identifierInput.getIdentifierInput()
            await this.checkNoDuplicatesInPatientIdentifiers(editedPatientIdentifier)
            this.errorMessage = this.duplicationError
            if (this.errorMessage) {
                this.internalPatientIdentifiers.splice(editedElementIndex, 0, originalElement)
            } else {
                this.internalPatientIdentifiers.splice(editedElementIndex, 0, editedPatientIdentifier)
                this.consolidateInternalIdentifiers()
                this.resetIdentifierToSave()
                this.editingIdentifier = false
                await this.$refs.identifierInput.resetValidator()
            }
        },
        consolidateInternalIdentifiers() {
            this.patientIdentifiers.splice(0, this.patientIdentifiers.length,
                ...this.toPatientIdentifierRequest(this.internalPatientIdentifiers)
            )
        },
        toPatientIdentifierRequest() {
            return this.internalPatientIdentifiers.map(patientIdentifier => {
                return {
                    identifierTypeId: patientIdentifier.identifierType.idtId,
                    paiId: patientIdentifier.paiId,
                    paiIdentifier: patientIdentifier.identifierValue,
                    centerId: patientIdentifier.center && patientIdentifier.center.id
                }
            })
        },
        removeIdentifier(key) {
            this.internalPatientIdentifiers.splice(key, 1)
            this.consolidateInternalIdentifiers()
        }
    }
}

</script>

<style lang="scss" scoped>

.error-message span{
  font-size: font-size(small);
}

.patient-id .multiselect{
  padding-top: 0 !important;
}

  .btn-vt-light{
    color: vt-color(primary) !important;
  }

  .btn-vt-light:hover{
    color: vt-color(dark-primary) !important;
  }

  .crud-id{
    max-height: 140px;
     overflow-y:auto;
    overflow-x: hidden;
    margin-top: -0.5rem;
    margin-left: 0;
    margin-right: -8px;
  }

  .crud-element{
    width: 100%;
    height: 25px;
    padding: 0;
  }

  .crud-element span{
    font-size: 0.875rem;
  }

</style>
